import React from 'react'
import { Maybe } from 'graphql/jsutils/Maybe'
import { paths } from '@/constants/paths'
import { Seo } from '@/layout/Seo'
import { useCanonicalUrl } from '@/layout/Seo/useCanonicalUrl/useCanonicalUrl'
import { LegalLayout } from '@/layouts/LegalLayout'
import { NextPageWithLayout } from '@/layouts/types'
import { Page } from '@/page'
import { getPageByPath } from '@/services/CmsService/Pages'
import { PageDataContext, PageModel, fetchPageDataContext } from '@/services/RenderService'
import { getStaticPropsErrorHandler } from '@/utils/nextUtils/nextErrorHandlers'
import { determinePageRevalidation } from '@/utils/nextUtils/revalidation'
import { getGenericStaticPaths } from '@/utils/nextUtils/staticPages'
import { loadTranslations } from '@/utils/translate/translate-server'

interface LegalIndexPageProps {
  pageDataContext: Maybe<PageDataContext>
  page: Maybe<PageModel>
}

const LegalIndexPage: NextPageWithLayout<LegalIndexPageProps> = (props) => {
  const canonical = useCanonicalUrl(paths.legal.index)
  const title = 'Legal | Angel'

  return (
    <div className="bg-white py-20 text-justify text-black">
      <Seo
        canonical={canonical}
        title={title}
        description="Legal information, terms of use, privacy policy, and other legal documents for Angel."
        openGraph={{
          title,
          url: canonical,
          site_name: 'Angel',
        }}
        path={paths.legal.index}
      />
      <main className="flex flex-col gap-8 p-10">
        <Page page={props.page} pageDataContext={props.pageDataContext} />
      </main>
    </div>
  )
}

LegalIndexPage.getLayout = (page: React.ReactElement) => {
  return <LegalLayout>{page}</LegalLayout>
}

export const getStaticProps = getStaticPropsErrorHandler<LegalIndexPageProps>(
  { filePath: 'pages/[locale]/legal/index.tsx' },
  async ({ locale, preview, federationClient }) => {
    const [page, translations] = await Promise.all([
      getPageByPath(paths.legal.index, { locale, preview }),
      loadTranslations(locale, ['common', 'account']),
    ])
    const pageDataContext = page ? await fetchPageDataContext(page, preview, locale, federationClient) : null

    return {
      props: {
        page,
        pageDataContext,
        ...translations,
      },
      revalidate: determinePageRevalidation(page, pageDataContext),
    }
  },
)

export const getStaticPaths = getGenericStaticPaths

export default LegalIndexPage
